import React, { useEffect, useState } from "react";
import { FiStar, FiCheckCircle, FiThumbsUp, FiEdit3 } from "react-icons/fi";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl"; // Ensure imgurl is imported for your profile images
import Skeleton from "react-loading-skeleton";
import Swal from "sweetalert2";

function ProductReview({ productId }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");

  const showToast = (icon, title) => {
    Swal.fire({
      toast: true,
      icon,
      title,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
  };

  // 1. Fetch Dynamic Data from API
  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${baseurl}getReviewsByProductId/${productId}`);
      if (res.data.success) {
        setReviews(res.data.reviews);
      }
    } catch (err) {
      console.error("Error fetching reviews:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productId) {
      fetchReviews();
    }
  }, [productId]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    const userId = localStorage.getItem("customerId"); // Matches your backend requirement
    if (!userId) return showToast("error", "Please login to write a review");
      console.log("productId", productId);
      console.log("userId", userId);
      console.log("rating", rating);
      console.log("comment", comment);

    try {
      const res = await axios.post(`${baseurl}addProductReview`, {
        productId,
        userId,
        rating,
        comment,
      });

      console.log("response", res);

      if (res.data.success) {
        showToast("success", "Review submitted for approval!");
        setComment("");
        setShowForm(false);
        fetchReviews(); // Refresh list
      }
    } catch (err) {
      showToast("error", "Failed to submit review");
    }
  };

  // Calculate Average Rating for UI
  const avgRating = reviews.length > 0 
    ? (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1) 
    : "0.0";

  if (loading) return <div className="p-4"><Skeleton count={3} height={120} className="mb-3" /></div>;

  return (
    <div className="review-section-container">
      <div className="row g-4">
        {/* --- LEFT SIDE: STATS --- */}
        <div className="col-lg-4">
          <div className="rating-summary-card">
            <h4 className="fw-bold mb-1">Customer Reviews</h4>
            <div className="d-flex align-items-center mb-3">
              <div className="big-rating-number">{avgRating}</div>
              <div className="ms-3">
                <div className="stars-gold">
                  {[...Array(5)].map((_, i) => (
                    <FiStar key={i} className={i < Math.round(avgRating) ? "fill" : ""} />
                  ))}
                </div>
                <div className="text-muted small">Based on {reviews.length} ratings</div>
              </div>
            </div>

            {[5, 4, 3, 2, 1].map((num) => {
              const count = reviews.filter((r) => r.rating === num).length;
              const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
              return (
                <div className="d-flex align-items-center mb-2" key={num}>
                  <span className="small fw-bold me-2" style={{ width: "12px" }}>{num}</span>
                  <FiStar size={12} className="me-2 text-muted" />
                  <div className="progress flex-grow-1" style={{ height: "6px" }}>
                    <div className="progress-bar bg-success" style={{ width: `${percentage}%` }}></div>
                  </div>
                  <span className="small text-muted ms-2">{Math.round(percentage)}%</span>
                </div>
              );
            })}

            <button className="btn-write-review mt-4" onClick={() => setShowForm(!showForm)}>
              <FiEdit3 className="me-2" /> Write a Review
            </button>
          </div>
        </div>

        {/* --- RIGHT SIDE: REVIEWS LIST & FORM --- */}
        <div className="col-lg-8">
          {showForm && (
            <div className="review-form-card mb-4 fade-in">
              <h5>Write a Review</h5>
              <form onSubmit={handleSubmitReview}>
                <div className="star-rating-input mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      type="button"
                      key={star}
                      className={star <= (hover || rating) ? "on" : "off"}
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHover(star)}
                      onMouseLeave={() => setHover(rating)}
                    >
                      <FiStar size={24} fill={star <= (hover || rating) ? "#ffc107" : "none"} />
                    </button>
                  ))}
                </div>
                <textarea
                  className="form-control mb-3"
                  rows="4"
                  placeholder="What did you like or dislike?"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  required
                ></textarea>
                <div className="d-flex gap-2">
                  <button type="submit" className="btn btn-dark px-4">Submit Review</button>
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                </div>
              </form>
            </div>
          )}

          <div className="reviews-list">
            <h5 className="fw-bold mb-4">Top Reviews</h5>
            {reviews.length === 0 ? (
              <div className="text-center py-5 border rounded bg-light">
                <p className="text-muted mb-0">No reviews yet. Be the first to share your thoughts!</p>
              </div>
            ) : (
              reviews.map((rev) => (
                <div className="review-item-card" key={rev.id}>
                  <div className="d-flex justify-content-between">
                    <div className="d-flex align-items-center mb-2">
                      {/* --- AVATAR LOGIC --- */}
                      <div className="user-avatar-small overflow-hidden">
                        {rev.profile_image ? (
                          <img 
                            src={`${imgurl}${rev.profile_image}`} 
                            alt={rev.user_name} 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                          />
                        ) : (
                          <span>{rev.user_name?.charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="ms-2">
                        <span className="fw-bold d-block">{rev.user_name}</span>
                        <span className="verified-badge">
                          <FiCheckCircle size={10} /> Verified Purchase
                        </span>
                      </div>
                    </div>
                    <small className="text-muted">
                        {new Date(rev.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </small>
                  </div>
                  <div className="stars-gold mb-2">
                    {[...Array(5)].map((_, i) => (
                      <FiStar key={i} className={i < rev.rating ? "fill" : ""} size={14} />
                    ))}
                  </div>
                  <p className="review-text">{rev.comment}</p>
                  {/* <div className="review-actions">
                    <button className="btn-helpful"><FiThumbsUp /> Helpful</button>
                    <button className="btn-report">Report</button>
                  </div> */}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      {/* Styles remain the same as previous response */}
        <style>{`
        .review-section-container { padding: 40px 0; border-top: 1px solid #eee; }
        
        /* Summary Card */
        .rating-summary-card {
          background: #fff; border: 1px solid #f0f0f0; padding: 25px; border-radius: 16px; position: sticky; top: 100px;
        }
        .big-rating-number { font-size: 3.5rem; font-weight: 800; color: #1a1a1a; line-height: 1; }
        .stars-gold { color: #ffc107; }
        .stars-gold .fill { fill: #ffc107; }
        .progress-bar { border-radius: 10px; }
        .btn-write-review {
          width: 100%; padding: 12px; border: 2px solid #1a1a1a; background: transparent; 
          border-radius: 10px; font-weight: 700; transition: 0.3s;
        }
        .btn-write-review:hover { background: #1a1a1a; color: #fff; }

        /* Form */
        .review-form-card { background: #f9f9f9; padding: 25px; border-radius: 16px; border: 1px solid #eee; }
        .star-rating-input { display: flex; gap: 5px; }
        .star-rating-input button { background: none; border: none; padding: 0; cursor: pointer; color: #ccc; transition: 0.2s; }
        .star-rating-input button.on { color: #ffc107; }

        /* Review Item */
        .review-item-card { border-bottom: 1px solid #f0f0f0; padding: 25px 0; }
        .review-item-card:last-child { border-bottom: none; }
        .user-avatar-small { 
          width: 35px; height: 35px; background: #eee; border-radius: 50%; 
          display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.8rem;
        }
        .verified-badge { font-size: 0.65rem; color: #28a745; font-weight: 700; background: #e8f9ee; padding: 2px 6px; border-radius: 4px; }
        .review-text { font-size: 0.95rem; color: #444; line-height: 1.6; }
        .review-actions { display: flex; gap: 15px; margin-top: 15px; }
        .btn-helpful, .btn-report { 
          background: none; border: 1px solid #eee; padding: 4px 12px; border-radius: 6px; 
          font-size: 0.8rem; font-weight: 600; color: #666; transition: 0.2s;
        }
        .btn-helpful:hover { border-color: #1a1a1a; color: #1a1a1a; }

        .fade-in { animation: fadeIn 0.4s ease-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

export default ProductReview;